import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import { useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import isFunction from 'lodash/isFunction';

import { PreparedApiErrorsType } from 'src/models/BaseApi';
import { dispatchAnyRequestStart } from 'src/store/anyRequest/helpers';
import { anyRequestSelector } from 'src/store/anyRequest/selectors';
import { anyRequestClear } from 'src/store/anyRequest/slice';
import { AnyRequestBaseStoreEntityType } from 'src/store/types';
import { showAnyErrors } from 'src/utils/errors';
import { getUniqueId } from 'src/utils/getUniqueId';
import { SafeAnyType } from 'src/utils/safeAny';
import { UseAnyRequestConfigType } from 'src/models/useAnyRequest';
import { isNotNull } from 'src/utils/types';

/**
 * @deprecated use useRequest{@see useRequest} or useOneRequest{@see useOneRequest}
 *
 * helper to extract data from any-request-store
 *  + cleaning up
 *  + showing errors (optional)
 *  + handle response data (optional)
 *
 *  uniqueId might be null if there is an apiFunction in config
 */
export const useAnyRequest = <Response = SafeAnyType, Error = PreparedApiErrorsType, Data = SafeAnyType>(
  uniqueId: string | null,
  config?: UseAnyRequestConfigType<Response, Error, Data>
): AnyRequestBaseStoreEntityType<Response, Error> => {
  const uniqueIdLocal: string = useMemo(
    () => uniqueId || get(config?.apiFunction, 'name', getUniqueId()),
    [config?.apiFunction, uniqueId]
  );

  const { showErrors = true, onSuccess, onError, apiFunction, data } = config || {};

  const processing = useRef(isFunction(apiFunction));

  const model: AnyRequestBaseStoreEntityType<Response, Error> = useSelector(anyRequestSelector(uniqueIdLocal));

  const dispatch = useDispatch();

  useEffect(() => {
    if (model.error) {
      if (showErrors) {
        showAnyErrors(model.error);
      }

      dispatch(anyRequestClear(uniqueIdLocal));
      processing.current = false;
    } else if (isNotNull(model.response)) {
      // different responses can be there
      dispatch(anyRequestClear(uniqueIdLocal));
      processing.current = false;
    }
  }, [dispatch, showErrors, model, uniqueIdLocal]);

  /** if you need to call api fn after a component is mounted */
  useEffect(() => {
    if (isFunction(apiFunction)) {
      dispatchAnyRequestStart({
        data,
        apiFunction,
        uniqueId: uniqueIdLocal,
        callerName: apiFunction.name, // let's use this as uniqueId some later
      });
    }
    // eslint-disable-next-line
  }, [uniqueId]);

  /** handle response data  */
  useEffect(() => {
    const { response, error } = model;

    if (error) {
      if (isFunction(onError)) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        onError(error, response);
      }
    } else if (isFunction(onSuccess) && isNotNull(response)) {
      onSuccess(response);
    }
  }, [model, onError, onSuccess]);

  /** avoid return a model with loading=false, when it actually is waiting for a response */
  if (processing.current) {
    const clonedModel = cloneDeep(model);
    clonedModel.loading = true;
    return clonedModel;
  }

  return model;
};
